import React from 'react';
import { PROVISIONING_STEPS } from './cockpit-data.jsx';
import { RepairActionModal } from './cockpit-health.jsx';
import { Icon } from './icons.jsx';
// Cockpit: Tab Provisionamento

const STEP_STATUS_CFG = {
  done:     { icon: 'check',   color: 'var(--success)', bg: 'var(--success-bg)',  label: 'Concluído'    },
  running:  { icon: 'refresh', color: 'var(--info)',    bg: 'var(--info-bg)',     label: 'Em andamento' },
  failed:   { icon: 'x',      color: 'var(--danger)',  bg: 'var(--danger-bg)',   label: 'Falhou'       },
  waiting:  { icon: 'clock',  color: 'var(--text-muted)', bg: 'var(--bg-subtle)', label: 'Aguardando'  },
  skipped:  { icon: 'minus',  color: 'var(--text-muted)', bg: 'var(--bg-subtle)', label: 'Pulado'      },
};

// ── ProvisioningStepRow ───────────────────────────────────────────────────────
const ProvisioningStepRow = ({ step, isLast, onViewLog, onRetry }) => {
  const sc = STEP_STATUS_CFG[step.status] || STEP_STATUS_CFG.waiting;
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
      {/* Linha vertical conectora */}
      {!isLast && (
        <div style={{ position: 'absolute', left: 15, top: 34, bottom: -4, width: 2, background: 'var(--border)', zIndex: 0 }} />
      )}

      {/* Dot */}
      <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: '50%', background: sc.bg, border: `2px solid ${sc.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: sc.color, position: 'relative', zIndex: 1, marginTop: 2 }}>
        <Icon name={sc.icon} size={14} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, paddingBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{step.name}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: sc.bg, color: sc.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{sc.label}</span>
          {step.duration !== '—' && (
            <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>{step.duration}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{step.ts}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn ghost sm" style={{ fontSize: 11, height: 24, padding: '0 8px' }} onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Ocultar log' : 'Ver log'}
            </button>
            {step.status === 'failed' && (
              <button className="btn sm" style={{ fontSize: 11, height: 24, padding: '0 8px', background: 'var(--danger-bg)', color: 'var(--danger)', border: 'none' }} onClick={() => onRetry(step)}>
                <Icon name="refresh" size={11} />Tentar novamente
              </button>
            )}
          </div>
        </div>
        {expanded && (
          <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 6, fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {'[' + step.ts + '] step.' + step.id + ': ' + step.msg}
          </div>
        )}
      </div>
    </div>
  );
};

// ── ProvisioningStatCard ──────────────────────────────────────────────────────
const ProvisioningStatCard = ({ icon, label, value, sub, color }) => (
  <div className="card" style={{ padding: '16px 18px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: color ? color + '18' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: color || 'var(--text-secondary)', flexShrink: 0 }}>
        <Icon name={icon} size={16} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: color || 'var(--text)', letterSpacing: '-0.01em' }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  </div>
);

// ── CockpitProvisionTab ───────────────────────────────────────────────────────
const CockpitProvisionTab = ({ cockpitData, onToast }) => {
  const [retryModal, setRetryModal] = React.useState(null);

  const allDone    = (cockpitData?.provisioningSteps || PROVISIONING_STEPS).every(s => s.status === 'done');
  const failedCount = (cockpitData?.provisioningSteps || PROVISIONING_STEPS).filter(s => s.status === 'failed').length;
  const totalMs   = '4.5s';

  return (
    <div>
      {/* Status banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 10, background: allDone ? 'var(--success-bg)' : 'var(--danger-bg)', border: `1px solid color-mix(in srgb, ${allDone ? 'var(--success)' : 'var(--danger)'} 25%, transparent)`, marginBottom: 20 }}>
        <Icon name={allDone ? 'check' : 'alert'} size={18} style={{ color: allDone ? 'var(--success)' : 'var(--danger)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: allDone ? 'var(--success)' : 'var(--danger)' }}>
            {allDone ? 'Provisionamento concluído com sucesso' : `Provisionamento com ${failedCount} falha(s)`}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            Executado em 02/11/2025 14:22 · Duração total: {totalMs} · 14/14 etapas concluídas
          </div>
        </div>
        <button className="btn" style={{ flexShrink: 0 }} onClick={() => onToast('Diagnóstico de provisionamento iniciado…')}>
          <Icon name="activity" size={14} />Diagnosticar
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <ProvisioningStatCard icon="check"   label="Último provisionamento" value="02/11/25"  sub="14:22:13"        color="var(--success)" />
        <ProvisioningStatCard icon="refresh" label="Tentativas de retry"    value="0"         sub="Sem retries"     color="var(--info)"    />
        <ProvisioningStatCard icon="trash"   label="Cleanup executado"       value="Não"       sub="Nenhum recurso removido" />
        <ProvisioningStatCard icon="alert"   label="Recursos órfãos"         value="0"         sub="Nenhum encontrado" />
      </div>

      {/* Timeline */}
      <div className="card">
        <div className="card-head">
          <Icon name="layers" size={15} style={{ color: 'var(--text-muted)' }} />
          <h3>Timeline de provisionamento</h3>
          <span className="sub">02/11/2025 — 14 etapas</span>
          <div className="actions">
            <button className="btn ghost sm" onClick={() => onToast('Log completo exportado')}>
              <Icon name="download" size={13} />Exportar log
            </button>
          </div>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {(cockpitData?.provisioningSteps || PROVISIONING_STEPS).map((step, i) => (
            <ProvisioningStepRow
              key={step.id}
              step={step}
              isLast={i === (cockpitData?.provisioningSteps || PROVISIONING_STEPS).length - 1}
              onViewLog={() => {}}
              onRetry={(s) => {
                onToast(`Retry de "${s.name}" iniciado…`);
              }}
            />
          ))}
        </div>
      </div>

      <RepairActionModal
        open={!!retryModal}
        action={retryModal}
        onClose={() => setRetryModal(null)}
        onConfirm={(motivo) => {
          onToast(`Retry de "${retryModal?.name}" iniciado`);
          setRetryModal(null);
        }}
      />
    </div>
  );
};

export {
  CockpitProvisionTab,
};
